import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectFilters } from "store/filters/selectors";
import { updateFilters, deleteFilters } from "store/filters/operations";
import { Checkbox } from "reusableComponents";
import { useTranslation } from "react-i18next";
import FiltersButton from "../FiltersButton";
import styles from "./styles.module.scss";

const PriceOrderFilterButton = () => {
  const dispatch = useDispatch();
  const [t] = useTranslation("global");
  const filters = useSelector(selectFilters);
  const [showOrderDropdown, setShowOrderDropdown] = useState(false);
  const dropdownRef = useRef(null);
  
  // Extract order-related filters
  const { order, order_type, order_price_type } = filters;
  const isActive = order_type === 'price' && (order === 'asc' || order === 'desc');

  // Available price types for ordering
  const priceTypeOptions = [
    { type: 'hour', key: 'search.orders.hour' },
    { type: 'half_day', key: 'search.orders.halfDay' },
    { type: 'day', key: 'search.orders.day' },
    { type: 'month', key: 'search.orders.month' }
  ];

  const toggleShowOrderDropdown = () => setShowOrderDropdown(!showOrderDropdown);

  const handleSelectOrderDirection = (direction) => {
    // If clicking the same direction, remove the filter
    if (order === direction && order_type === 'price') {
      dispatch(deleteFilters('order', 'order_type', 'order_price_type'));
    } else {
      // Set new order direction and type
      const newFilters = {
        order: direction,
        order_type: 'price'
      };
      
      // Keep the existing price type if it was already selected
      if (order_price_type) {
        newFilters.order_price_type = order_price_type;
      }
      
      dispatch(updateFilters(newFilters));
    }
  };

  const handleSelectPriceType = (priceType) => {
    // If clicking the same price type, remove it
    if (order_price_type === priceType) {
      if (order && order_type === 'price') {
        // Keep order direction, remove only price type
        dispatch(updateFilters({ order, order_type: 'price' }));
        dispatch(deleteFilters('order_price_type'));
      } else {
        dispatch(deleteFilters('order_price_type'));
      }
    } else {
      // Set new price type
      const newFilters = { order_price_type: priceType };
      
      // If we have order direction already, maintain it
      if (order && order_type === 'price') {
        newFilters.order = order;
        newFilters.order_type = 'price';
      }
      
      dispatch(updateFilters(newFilters));
    }
  };

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOrderDropdown(false);
      }
    };

    if (showOrderDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showOrderDropdown]);

  // Get button label based on current selection
  const getButtonLabel = () => {
    if (order_type === 'price') {
      const direction = order === 'asc' ? t('search.orders.lowToHigh') : t('search.orders.highToLow');
      const priceType = order_price_type ? ` (${t(`search.orders.${order_price_type}`)})` : '';
      return `${direction}${priceType}`;
    }
    return t('search.sortByPrice');
  };

  return (
    <div className={styles.orderWrapper} ref={dropdownRef}>
      <FiltersButton
        label={getButtonLabel()}
        isActive={isActive}
        onClick={toggleShowOrderDropdown}
      />
      {showOrderDropdown && (
        <div className={styles.orderDropdownBody}>
          {/* Order Direction Section */}
          <div className={styles.orderSection}>
            <h4 className={styles.sectionTitle}>{t('search.orders.orderBy')}</h4>
            <div className={styles.orderOptionList}>
              <div className={styles.orderOption}>
                <Checkbox
                  onChange={() => handleSelectOrderDirection('asc')}
                  checked={order === 'asc' && order_type === 'price'}
                  label={t('search.orders.lowToHigh')}
                />
              </div>
              <div className={styles.orderOption}>
                <Checkbox
                  onChange={() => handleSelectOrderDirection('desc')}
                  checked={order === 'desc' && order_type === 'price'}
                  label={t('search.orders.highToLow')}
                />
              </div>
            </div>
          </div>

          {/* Price Type Section - Only show if order direction is selected */}
          {(order === 'asc' || order === 'desc') && order_type === 'price' && (
            <>
              <hr className={styles.divider} />
              <div className={styles.orderSection}>
                <h4 className={styles.sectionTitle}>{t('search.orders.priceType')}</h4>
                <div className={styles.priceTypeList}>
                  {priceTypeOptions.map((option) => (
                    <div key={option.type} className={styles.priceTypeOption}>
                      <Checkbox
                        onChange={() => handleSelectPriceType(option.type)}
                        checked={order_price_type === option.type}
                        label={t(option.key)}
                      />
                    </div>
                  ))}
                </div>
                <p className={styles.helperText}>
                  {t('search.orders.helperText')}
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PriceOrderFilterButton;